import { useBrandLocale } from '../../../../hooks/useBrandLocale';
import { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { useQuery, useMutation } from '@apollo/client';
import { toast } from 'react-toastify';
import { GET_PUBLIC_LOCATIONS } from '../../../../services/locationServices/queries';
import {
  GET_CAR_DELIVERY_LOCATIONS,
  REMOVE_CAR_DELIVERY_LOCATION,
  SET_CAR_DELIVERY_LOCATION,
} from '../../../../services/carService/queries';
import { formatAddress } from '../../../../helpers/formatAddress';
import { LocationType } from '../../../../../__generated__/graphql';

const Section = styled.div`
  ${tw`
    w-full
    bg-white
    rounded-lg
    p-6
    shadow-card
    border
    border-border-default
  `}
`;

const SectionTitle = styled.h3`
  ${tw`
    text-xl
    font-semibold
    mb-2
  `}
`;

const SectionDescription = styled.p`
  ${tw`
    text-sm
    text-text-secondary
    mb-6
  `}
`;

const Row = styled.div`
  ${tw`
    flex
    flex-col
    md:flex-row
    md:items-center
    gap-3
    mb-4
  `}
`;

const Label = styled.label`
  ${tw`
    text-sm
    font-medium
    text-text-secondary
  `}
`;

const Select = styled.select`
  ${tw`
    border
    border-border-default
    rounded-md
    px-3
    py-2
    w-full
  `}
`;

const Input = styled.input`
  ${tw`
    border
    border-border-default
    rounded-md
    px-3
    py-2
    w-full
  `}
`;

const Button = styled.button<{ $variant?: 'primary' | 'danger' }>`
  ${tw`
    px-4
    py-2
    rounded-md
    text-sm
    font-semibold
    transition-colors
    duration-200
  `}
  ${({ $variant }) =>
    $variant === 'danger'
      ? tw`bg-red-50 text-red-600 hover:bg-red-100`
      : tw`bg-black text-white hover:bg-black`}
`;

const List = styled.div`
  ${tw`
    flex
    flex-col
    gap-3
  `}
`;

const ListItem = styled.div`
  ${tw`
    border
    border-border-default
    rounded-md
    p-4
    flex
    flex-col
    gap-3
  `}
`;

const ListHeader = styled.div`
  ${tw`
    flex
    flex-col
    gap-1
  `}
`;

const ListTitle = styled.div`
  ${tw`
    font-semibold
  `}
`;

const ListSub = styled.div`
  ${tw`
    text-sm
    text-text-secondary
  `}
`;

const ListMeta = styled.div`
  ${tw`
    text-sm
    text-text-secondary
  `}
`;

const InlineRow = styled.div`
  ${tw`
    flex
    flex-col
    md:flex-row
    md:items-center
    gap-3
  `}
`;

const LimitNote = styled.div`
  ${tw`
    text-xs
    text-text-secondary
    mt-2
  `}
`;

type DeliveryLocation = {
  id: string;
  fee: number;
  isActive: boolean;
  location: {
    id: number;
    name: string;
    address: string;
    city: string;
    state: string;
    zipCode: string;
  };
};

type PublicLocation = {
  id: number;
  name: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
};

type Props = {
  carId: string;
};

export function DeliveryLocations({ carId }: Props) {
  // Spec 028: the fee is in the brand's currency (shown with "$" either way, D14).
  const currencyCode = useBrandLocale().currency;
  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  const { data: publicLocationsData, loading: publicLocationsLoading } = useQuery(
    GET_PUBLIC_LOCATIONS,
    {
      variables: { type: LocationType.Airport },
    },
  );

  const {
    data: deliveryLocationsData,
    loading: deliveryLocationsLoading,
    refetch: refetchDeliveryLocations,
  } = useQuery(GET_CAR_DELIVERY_LOCATIONS, {
    variables: { carId },
  });

  const [setCarDeliveryLocation, { loading: savingDeliveryLocation }] =
    useMutation(SET_CAR_DELIVERY_LOCATION);
  const [removeCarDeliveryLocation, { loading: removingDeliveryLocation }] = useMutation(
    REMOVE_CAR_DELIVERY_LOCATION,
  );

  const deliveryLocations: DeliveryLocation[] =
    deliveryLocationsData?.getCarDeliveryLocations || [];
  const publicLocations: PublicLocation[] = publicLocationsData?.publicLocations || [];

  const [selectedLocationId, setSelectedLocationId] = useState<number | ''>('');
  const [newFee, setNewFee] = useState<string>('0');
  const [editedFees, setEditedFees] = useState<Record<string, string>>({});
  const [editedActive, setEditedActive] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const nextFees: Record<string, string> = {};
    const nextActive: Record<string, boolean> = {};
    deliveryLocations.forEach(item => {
      nextFees[item.id] = item.fee.toString();
      nextActive[item.id] = item.isActive;
    });
    setEditedFees(nextFees);
    setEditedActive(nextActive);
  }, [deliveryLocations]);

  const availableLocations = useMemo(() => {
    const usedIds = new Set(deliveryLocations.map(item => item.location.id));
    return publicLocations.filter(location => !usedIds.has(location.id));
  }, [deliveryLocations, publicLocations]);

  const maxReached = deliveryLocations.length >= 10;

  const handleAdd = async () => {
    if (!selectedLocationId) {
      toast.error('Please select an airport location');
      return;
    }
    const feeValue = Number(newFee);
    if (Number.isNaN(feeValue) || feeValue < 0) {
      toast.error('Fee must be a valid non-negative number');
      return;
    }

    try {
      await setCarDeliveryLocation({
        variables: {
          input: {
            carId,
            locationId: Number(selectedLocationId),
            fee: feeValue,
            isActive: true,
          },
        },
      });
      setSelectedLocationId('');
      setNewFee('0');
      await refetchDeliveryLocations();
      toast.success('Delivery location added');
    } catch (error: any) {
      toast.error(error.message || 'Failed to add delivery location');
    }
  };

  const handleSave = async (item: DeliveryLocation) => {
    const feeValue = Number(editedFees[item.id]);
    if (Number.isNaN(feeValue) || feeValue < 0) {
      toast.error('Fee must be a valid non-negative number');
      return;
    }

    try {
      await setCarDeliveryLocation({
        variables: {
          input: {
            carId,
            locationId: item.location.id,
            fee: feeValue,
            isActive: editedActive[item.id],
          },
        },
      });
      await refetchDeliveryLocations();
      toast.success('Delivery location updated');
    } catch (error: any) {
      toast.error(error.message || 'Failed to update delivery location');
    }
  };

  const handleRemove = async (item: DeliveryLocation) => {
    try {
      await removeCarDeliveryLocation({
        variables: { input: { carId, locationId: item.location.id } },
      });
      await refetchDeliveryLocations();
      toast.success('Delivery location removed');
    } catch (error: any) {
      toast.error(error.message || 'Failed to remove delivery location');
    }
  };

  return (
    <Section>
      <SectionTitle>Delivery Locations</SectionTitle>
      <SectionDescription>
        Add up to 10 public airport locations where you can deliver this car and set a delivery fee.
      </SectionDescription>

      <Row>
        <div tw="w-full">
          <Label>Airport</Label>
          <Select
            value={selectedLocationId}
            onChange={event =>
              setSelectedLocationId(event.target.value ? Number(event.target.value) : '')
            }
            disabled={publicLocationsLoading || maxReached}
          >
            <option value="">Select an airport</option>
            {availableLocations.map(location => (
              <option key={location.id} value={location.id}>
                {location.name} - {location.city}, {location.state}
              </option>
            ))}
          </Select>
        </div>
        <div tw="w-full md:w-48">
          <Label>Delivery fee ({currencyCode})</Label>
          <Input
            type="number"
            min="0"
            step="1"
            value={newFee}
            onChange={event => setNewFee(event.target.value)}
            disabled={maxReached}
          />
        </div>
        <div tw="md:self-end">
          <Button onClick={handleAdd} disabled={maxReached || savingDeliveryLocation}>
            Add location
          </Button>
        </div>
      </Row>
      <LimitNote>
        {maxReached
          ? 'You reached the maximum of 10 delivery locations.'
          : `${deliveryLocations.length}/10 delivery locations used.`}
      </LimitNote>

      {deliveryLocationsLoading ? (
        <div tw="text-sm text-text-secondary mt-4">Loading delivery locations...</div>
      ) : (
        <List>
          {deliveryLocations.map(item => (
            <ListItem key={item.id}>
              <ListHeader>
                <ListTitle>{item.location.name}</ListTitle>
                <ListSub>{formatAddress(item.location)}</ListSub>
                <ListMeta>Delivery fee: {formatCurrency(item.fee)}</ListMeta>
              </ListHeader>
              <InlineRow>
                <div tw="w-full md:w-48">
                  <Label>Delivery fee ({currencyCode})</Label>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={editedFees[item.id] ?? '0'}
                    onChange={event =>
                      setEditedFees(prev => ({ ...prev, [item.id]: event.target.value }))
                    }
                  />
                </div>
                <div tw="w-full md:w-32">
                  <Label>Status</Label>
                  <Select
                    value={editedActive[item.id] ? 'active' : 'inactive'}
                    onChange={event =>
                      setEditedActive(prev => ({
                        ...prev,
                        [item.id]: event.target.value === 'active',
                      }))
                    }
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </Select>
                </div>
                <div tw="flex gap-2 md:ml-auto">
                  <Button onClick={() => handleSave(item)} disabled={savingDeliveryLocation}>
                    Save
                  </Button>
                  <Button
                    $variant="danger"
                    onClick={() => handleRemove(item)}
                    disabled={removingDeliveryLocation}
                  >
                    Remove
                  </Button>
                </div>
              </InlineRow>
            </ListItem>
          ))}
        </List>
      )}
    </Section>
  );
}
